// MobileTabBar — the phone bottom tab bar (Mobile Parity Program,
// Wave 0 slice 1). Renders nothing above 768px (CSS hides it; desktop
// keeps the top bar + left rail untouched).
//
// Five slots: Home · Households · Ask Stew (center action) · role-aware
// work queue · More. "More" opens the SAME full off-canvas nav drawer
// the ☰ button opens — the bar is the thumb-reach shortlist, the drawer
// is the whole OS, so nothing becomes unreachable (full-parity bar).
// Which links render, and for whom, is pure data in
// mobile-tab-bar-data.ts.

import {
  CalendarCheck,
  CheckSquare,
  Circle,
  Columns3,
  Handshake,
  Home,
  Menu,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import type { SessionUser } from "../api";
import { useChrome } from "./ChromeContext";
import {
  activeMobileTabKey,
  pickMobileTabs,
  type MobileTab,
  type MobileTabIcon,
} from "./mobile-tab-bar-data";

const TAB_ICONS: Record<MobileTabIcon, LucideIcon> = {
  home: Home,
  households: Users,
  renewals: RefreshCw,
  processing: Columns3,
  opportunities: Circle,
  tasks: CheckSquare,
  today: CalendarCheck,
  referrals: Handshake,
};

function TabLink({ tab, isActive }: { tab: MobileTab; isActive: boolean }) {
  const Icon = TAB_ICONS[tab.icon];
  return (
    <Link
      to={tab.to}
      className={`mobile-tab-bar-item${isActive ? " is-active" : ""}`}
      aria-current={isActive ? "page" : undefined}
    >
      <Icon size={21} strokeWidth={isActive ? 2.25 : 1.75} aria-hidden="true" />
      <span className="mobile-tab-bar-label">{tab.label}</span>
    </Link>
  );
}

export function MobileTabBar({ user }: { user: SessionUser }) {
  const location = useLocation();
  const chrome = useChrome();
  const tabs = useMemo(
    () => pickMobileTabs(user),
    // Same memo posture as TopNav's visibleNavItems (Sentry
    // JAVASCRIPT-REACT-3N): key on the fields the data actually reads,
    // not the session object's identity.
    [user?.employee_role_slug, user?.editable_company_slugs],
  );
  const activeKey = activeMobileTabKey(location.pathname, tabs);

  const moreButton = (
    <button
      type="button"
      className="mobile-tab-bar-item"
      aria-label="Open full navigation menu"
      aria-expanded={chrome.isMobileNavOpen}
      onClick={chrome.openMobileNav}
    >
      <Menu size={21} strokeWidth={1.75} aria-hidden="true" />
      <span className="mobile-tab-bar-label">More</span>
    </button>
  );

  // Ambassador (single-destination role): just their lane + More — no
  // center Ask Stew action (firm-wide search is not theirs).
  if (tabs.length === 1) {
    return (
      <nav className="mobile-tab-bar" aria-label="Phone navigation">
        <TabLink tab={tabs[0]!} isActive={activeKey === tabs[0]!.key} />
        {moreButton}
      </nav>
    );
  }

  const [homeTab, householdsTab, workTab] = tabs;
  return (
    <nav className="mobile-tab-bar" aria-label="Phone navigation">
      {homeTab && <TabLink tab={homeTab} isActive={activeKey === homeTab.key} />}
      {householdsTab && (
        <TabLink
          tab={householdsTab}
          isActive={activeKey === householdsTab.key}
        />
      )}
      <button
        type="button"
        className="mobile-tab-bar-item mobile-tab-bar-stew"
        aria-label="Search and Ask Stew"
        onClick={chrome.openAskStew}
      >
        <span className="mobile-tab-bar-stew-circle" aria-hidden="true">
          <Search size={20} strokeWidth={2} />
        </span>
        <span className="mobile-tab-bar-label">Ask Stew</span>
      </button>
      {workTab && <TabLink tab={workTab} isActive={activeKey === workTab.key} />}
      {moreButton}
    </nav>
  );
}
